import {
  AgentComponentMode,
  AgentComponentType,
  MAX_SKILLS_PER_AGENT_TEMPLATE,
} from '@reach/shared-types/agent-components';
import { AgentTemplateKind } from '@reach/shared-types/agents';

interface ModeCopy {
  label: string;
  description: string;
}

interface ComponentPartCopy {
  title: string;
  description: string;
}

interface ComponentPlaceholders {
  name?: string;
  description?: string;
  trigger?: string;
  instructions?: string;
  tenantName?: string;
  tenantDescription?: string;
}

interface ComponentTypeCopy {
  title: string;
  description: string;
  typeLabel: string;
  newLabel: string;
  createLabel: string;
  addLabel: string;
  nameLabel: string;
  instructionsHelp: string;
  templateDescription: string;
  emptyNote: string;
  loadErrorNote: string;
  notFound: string;
  deleteBody: (componentName: string) => string;
  parts: readonly ComponentPartCopy[];
}

export interface TemplatePartCopy {
  title: string;
  tagline: string;
  description: string;
  examples: readonly string[];
}

export const NAV_COPY = {
  title: 'Agents',
  navLabel: 'Agent builder sections',
  overview: 'Overview',
  componentsHeading: 'Components',
  skills: 'Skills',
  guidance: 'Guidance',
  buildingBlocksHeading: 'Building blocks',
  tools: 'Tools',
} as const;

export const STATUS_COPY = {
  loading: 'Loading…',
  loadMore: 'Load more',
  loadError: (subject: string) => `${subject} could not be loaded. Refresh the page to try again.`,
} as const;

export const MODE_COPY = {
  [AgentComponentMode.HIDDEN]: {
    label: 'Hidden',
    description: 'The agent uses it. Tenants never see it.',
  },
  [AgentComponentMode.ALWAYS_ON]: {
    label: 'Always on',
    description: "Tenants see it, marked as set by you, and can't turn it off.",
  },
  [AgentComponentMode.DEFAULT_ON]: {
    label: 'On by default',
    description: 'Tenants see a switch that starts on. They can turn it off.',
  },
  [AgentComponentMode.DEFAULT_OFF]: {
    label: 'Off by default',
    description: 'Tenants see a switch that starts off. They can turn it on.',
  },
} as const satisfies Record<AgentComponentMode, ModeCopy>;

export const COMPONENT_TYPE_COPY = {
  [AgentComponentType.SKILL]: {
    title: 'Skills',
    description:
      'Workflows your agent runs only when a specific situation comes up, like booking an appointment or taking in a new client.',
    typeLabel: 'Skill',
    newLabel: 'New skill',
    createLabel: 'Create skill',
    addLabel: 'Add skill',
    nameLabel: 'Skill name',
    instructionsHelp: 'The steps the agent follows once this situation comes up.',
    templateDescription: `Workflows the agent runs when their situation comes up. A template holds up to ${MAX_SKILLS_PER_AGENT_TEMPLATE} skills.`,
    emptyNote: 'No skills yet.',
    loadErrorNote: STATUS_COPY.loadError('Your skills'),
    notFound: 'This skill could not be found. It may have been deleted.',
    deleteBody: componentName =>
      `${componentName} will be removed from your skills. This can't be undone.`,
    parts: [
      { title: 'When to run', description: 'The situation that starts this workflow.' },
      { title: 'Instructions', description: 'The steps the agent follows, in order.' },
      {
        title: 'Tools',
        description: 'Optional. Lets a step look things up or make changes in your system.',
      },
    ],
  },
  [AgentComponentType.GUIDANCE]: {
    title: 'Guidance',
    description:
      'What your agent always does, and never does, in every conversation. For something it does only in a specific situation, use a skill.',
    typeLabel: 'Guidance',
    newLabel: 'New guidance',
    createLabel: 'Create guidance',
    addLabel: 'Add guidance',
    nameLabel: 'Guidance name',
    instructionsHelp: 'The base instructions the agent always follows, including what it must never do.',
    templateDescription: 'Rules the agent follows in every conversation.',
    emptyNote: 'No guidance yet.',
    loadErrorNote: STATUS_COPY.loadError('Your guidance'),
    notFound: 'This guidance could not be found. It may have been deleted.',
    deleteBody: componentName =>
      `${componentName} will be removed from your guidance. This can't be undone.`,
    parts: [
      { title: 'Instructions', description: 'The base instructions an agent always follows.' },
      { title: 'Tools', description: 'Optional API calls the agent can make while following it.' },
    ],
  },
  [AgentComponentType.ESCALATION]: {
    title: 'Escalations',
    description: "When agents hand the call to a person, at each tenant's transfer number.",
    typeLabel: 'Escalation',
    newLabel: 'New escalation',
    createLabel: 'Create escalation',
    addLabel: 'Add escalation',
    nameLabel: 'Escalation name',
    instructionsHelp:
      "When the agent should hand the call to a person. The call goes to the tenant's transfer number.",
    templateDescription: "When the agent hands the call to a person, at each tenant's transfer number.",
    emptyNote: 'No escalations yet.',
    loadErrorNote: STATUS_COPY.loadError('Your escalations'),
    notFound: 'This escalation could not be found. It may have been deleted.',
    deleteBody: componentName =>
      `${componentName} will be removed from your escalations. This can't be undone.`,
    parts: [
      {
        title: 'Instructions',
        description: 'When to hand the call to a person.',
      },
      {
        title: 'Destination',
        description: "Each tenant's transfer number. Every escalation goes there.",
      },
    ],
  },
} as const satisfies Record<AgentComponentType, ComponentTypeCopy>;

export const COMPONENT_PLACEHOLDERS: Record<AgentComponentType, ComponentPlaceholders> = {
  [AgentComponentType.GUIDANCE]: {
    name: 'e.g. Client lookup',
    description:
      'e.g. Silently checks whether the caller is an existing client, so the right booking skill runs',
    instructions: [
      'e.g. Before booking, find out whether the caller is an existing client.',
      '',
      '1. If you already know the caller is an existing client, go straight to booking.',
      "2. Otherwise, call Find Client once with the caller's phone number. Don't say you're looking them up. Keep the conversation going.",
      "3. Treat the caller as an existing client only if Find Client confirms a client record. Any other result, including no match or an error, means they're a new client.",
      '',
      'Never stop or delay a booking because of this check, and never mention it to the caller.',
    ].join('\n'),
    tenantName: 'e.g. Recognize returning clients',
    tenantDescription:
      'e.g. Checks whether a caller is already your client, so returning clients book faster.',
  },
  [AgentComponentType.SKILL]: {
    name: 'e.g. Book appointments',
    description: 'e.g. Books a caller into an open time',
    trigger: 'e.g. The caller wants to book an appointment.',
    instructions: [
      'e.g.',
      "1. Ask which service they want and when they'd like to come in.",
      '2. Offer up to three open times from Find Open Times.',
      '3. Book the time they choose with Book Appointment.',
      '4. If Book Appointment says details are missing, collect them, call Create Client if it asks for a client record, then try again.',
      '5. Confirm the booking only after Book Appointment succeeds.',
    ].join('\n'),
    tenantName: 'e.g. Book appointments',
    tenantDescription: 'e.g. Books callers into open times on your calendar.',
  },
  [AgentComponentType.ESCALATION]: {
    name: 'e.g. Safety emergency',
    description: 'e.g. Gets callers with an urgent safety risk to a person right away',
    instructions:
      "e.g. Transfer right away when the caller describes an active safety risk, like a burning smell, sparks, flooding or a gas leak. Don't finish intake or ask more questions first.",
    tenantName: 'e.g. Safety emergencies',
    tenantDescription: 'e.g. Sends callers with an urgent safety risk straight to your team.',
  },
};

const describeCount = (count: number, singular: string, plural: string): string => {
  const countIsOne = count === 1;
  return countIsOne ? `1 ${singular}` : `${count} ${plural}`;
};

export const LIST_COPY = {
  nameColumn: 'Name',
  toolsColumn: 'Tools',
  usedInColumn: 'Used in',
  visibilityColumn: 'Visibility',
  noTools: 'No tools',
  notUsed: 'Not used',
  toolCount: (toolCount: number) => describeCount(toolCount, 'tool', 'tools'),
} as const;

export const TEMPLATE_KIND_NAMES = {
  [AgentTemplateKind.VOICE]: 'Voice',
  [AgentTemplateKind.SMS]: 'SMS',
} as const satisfies Record<AgentTemplateKind, string>;

/** The order template kinds are listed in, wherever a row names more than one. */
export const TEMPLATE_KIND_ORDER = Object.keys(TEMPLATE_KIND_NAMES) as AgentTemplateKind[];

export const OVERVIEW_COPY = {
  intro:
    'Define reusable components and tools once, then add them to an agent template. Each tenant gets their own copy of that template, with their business details and a few choices of their own.',
  stepsHeading: 'How it fits together',
  steps: [
    {
      title: 'Build components and add tools',
      description:
        'Write the guidance, skills and escalations your agents use. Add the tools that connect them to your API.',
    },
    {
      title: 'Add them to an agent template',
      description:
        'Choose which components each agent template uses. Tenants on that template get them.',
    },
  ],
  partsHeading: 'What a template is made of',
  componentsHeading: 'Components',
  componentsDescription:
    'Components are built once, and you decide which agent templates use them. Each one also sets whether tenants see it.',
  toolsHeading: 'Tools',
  toolsDescription:
    'Tools are built once and shared. Any skill or guidance can use them to act in your system.',
  exampleLabel: (exampleCount: number) => (exampleCount === 1 ? 'Example:' : 'Examples:'),
  modesHeading: 'What tenants can change',
  modesDescription: 'Set on each component under Visibility & control.',
  skillCount: (skillCount: number) => describeCount(skillCount, 'skill', 'skills'),
  guidanceCount: (guidanceCount: number) =>
    describeCount(guidanceCount, 'guidance rule', 'guidance rules'),
  escalationCount: (escalationCount: number) =>
    describeCount(escalationCount, 'escalation', 'escalations'),
  viewSkills: 'View skills',
  viewGuidance: 'View guidance',
  viewEscalations: 'View escalations',
  toolsLink: 'View tools',
} as const;

export const TOOL_LIST_COPY = {
  title: 'Tools',
  description:
    'A tool is one call to your API. Skills use tools to act in your system, and one tool can be shared by many skills.',
  newTool: 'New tool',
  filterLabel: 'Status filter',
  filters: { active: 'Active', inactive: 'Inactive', all: 'All' },
  toolColumn: 'Tool',
  requestColumn: 'Request',
  usedInColumn: LIST_COPY.usedInColumn,
  statusColumn: 'Status',
  lookupRequest: 'Synced schema lookup',
  notUsed: LIST_COPY.notUsed,
  templateUse: (templateKindName: string) => `${templateKindName} template`,
  publishedBehindLatest: (publishedVersion: number, latestVersion: number) =>
    `Synced templates use v${publishedVersion} · latest v${latestVersion}`,
  status: { active: 'Active', inactive: 'Inactive' },
  emptyNotes: { active: 'No active tools.', inactive: 'No inactive tools.', all: 'No tools yet.' },
  loadErrorNote: STATUS_COPY.loadError('Your tools'),
} as const;

export const TEMPLATE_PART_COPY = {
  guidance: {
    title: 'Guidance',
    tagline: 'Rules for every conversation',
    description: 'The base instructions an agent always follows, in every conversation.',
    examples: ["Always confirm the caller's name. Never give discounted pricing over the phone."],
  },
  skills: {
    title: 'Skills',
    tagline: 'Workflows for specific situations',
    description:
      'Each skill has the situation that starts it, the steps to follow, and optional tools that call your API.',
    examples: ['Book new appointments', 'New customer intake'],
  },
  escalations: {
    title: 'Escalations',
    tagline: 'When to hand off to a person',
    description:
      "When the agent should hand the call to a person. Every escalation transfers to the tenant's transfer number.",
    examples: ['Upset or angry caller'],
  },
  tools: {
    title: 'Tools',
    tagline: 'How components reach your API',
    description: 'Each tool makes one call to your API.',
    examples: ['Find open times'],
  },
} as const satisfies Record<'guidance' | 'skills' | 'escalations' | 'tools', TemplatePartCopy>;
